import { useEffect, useMemo, useState } from 'react';
import integration from '@sport/integration';
import Button from '../Button/Button';
import Logo from '../Logo/Logo';
import { isBetterHost, isIframeTurkeyHost } from '../../hostsConfig';
import Select from '../Select/Select';
import { useLanguageService } from '../../translations';
import useViewMode from '../../hooks/useViewMode';
import { ReactComponent as AskSvg } from '../../svg/ask.svg';
import { ReactComponent as ExitIcon } from '../../svg/exit_icon.svg';
import { ReactComponent as SearchSvg } from '../../svg/search.svg';
import { ReactComponent as SettingsIcon } from '../../svg/settings.svg';
import { getFormattedBalance } from '../../helpers/index';

const DEFAULT_CURRENCY = 'USD';

function Header({
  refresh = () => {},
  user,
  logout,
  login,
  changeCurrency,
  currency,
  hideControls,
  settingsClick,
  currentTheme,
}) {
  const { t } = useLanguageService();
  const { isWideView } = useViewMode();

  const userCurrencies = useMemo(
    () => user?.currencies || [DEFAULT_CURRENCY],
    [user],
  );
  const currencies = userCurrencies.map((currency) => ({
    name: currency,
    alias: currency,
    ...((user?.balances ?? user?.balance) && {
      total: user?.balances?.[currency] ?? user?.balance?.[currency],
    }),
  }));

  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    if (currency || userCurrencies.length <= 1) return;
    const timeout = setTimeout(() => setUseFallback(true), 5000);
    return () => clearTimeout(timeout);
  }, [currency, userCurrencies]);

  const activeCurrency = useMemo(() => {
    if (user) {
      return userCurrencies.length > 1 ?
          currencies.find(({ name }) => name === (currency || (useFallback && DEFAULT_CURRENCY))) :
          currencies[0]
    }
  }, [user, currency, currencies, userCurrencies, useFallback]);

  const onClick = (e) => {
    e.preventDefault();
  };

  const onSearchClick = (e) => {
    e.preventDefault();
    integration.emit({ path: '/search' }, 'iframe-navigation');
  };

  const displayCurrency = currency || DEFAULT_CURRENCY;

  const labelElement = (
    <div className="balance-wrapper">
      <span className="balance-count">
        <bdi>{`${getFormattedBalance(user?.balances?.[displayCurrency] || user?.balance)}`}</bdi> {displayCurrency}
      </span>
    </div>
  );

  return (
    <header className={`App-header ${isBetterHost ? 'beter' : ''}`}>
      <Logo currentTheme={currentTheme} refresh={refresh} />
      {!hideControls && (
        <>
          {!isIframeTurkeyHost && user && (
            <div className="header-item right">
              {activeCurrency && (
                <Select
                  items={currencies}
                  activeItem={activeCurrency}
                  labelElement={labelElement}
                  onSelect={({ name }) => {
                    changeCurrency(name);
                  }}
                />
              )}
              <Button action={settingsClick} type={['rounded', 'primary']}>
                <SettingsIcon />
              </Button>
              <Button
                action={logout}
                type={['rounded', 'login']}
              >
                <ExitIcon />
              </Button>
            </div>
          )}
          {!isIframeTurkeyHost && !user && (
            <div className="header-item right">
              <Button action={settingsClick} type={'primary'}>
                {t('Settings')} <SettingsIcon />
              </Button>
              <Button action={login} type={[isBetterHost && 'secondary', 'login']}>
                {t('Log in')}
              </Button>
            </div>
          )}

          {isIframeTurkeyHost && (
            <div className="header-item right">
              {isWideView && (
                <div className="header-item__block">
                  <Button action={onSearchClick} type={'ternary'}>
                    <SearchSvg />
                  </Button>
                </div>
              )}
              {!user && (
                <div className="header-item__block">
                  <Button action={login} type={'ternary'}>
                    {t('Log in')}
                  </Button>
                  <Button action={login} type={'yellow'}>
                    <div>
                      <span>{t('Register')}</span>
                      <span>{t('Register')}</span>
                      <span className="button-text">{t('Register')}</span>
                    </div>
                  </Button>
                </div>
              )}
              {user && (
                <div className="header-item__block">
                  <Button action={logout} type={['rounded', 'yellow', 'login']}>
                    <ExitIcon />
                  </Button>
                </div>
              )}
              {isWideView && (
                <div className="header-item__block">
                  <Button action={onClick} type={'ternary'}>
                    <AskSvg />
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </header>
  );
}

export default Header;
